import { Optional, TipoCliente, zod } from '@acme/core'
import { CreateCustomerBody as CustomerInput } from '@cel_cash/core/schemas'
import type {
    Customer,
    StatusCustomer as CustomerStatus,
    Periodicity,
} from '@cel_cash/core/types'
import { z } from 'zod'
import ApiService from '../api.service'
import { AvaliacaoSchema } from './avaliacao'

interface PlanoDiferenciado
    extends z.infer<typeof zod.PlanoDiferenciadoSchema> {
    periodicidade: Periodicity
}

export type Cliente = z.infer<typeof zod.ClienteSchema> & {
    status?: CustomerStatus
    categoriaId?: number
    tipo: TipoCliente
    PlanoDiferenciado?: PlanoDiferenciado
}
export type Modulo = {
    id: number
    clienteId: number
    href: string
    nome: string
    subject: string
    permitido: boolean
}

const service = new ApiService<Cliente>('clientes')
const celcash = new ApiService<Optional<CustomerInput, 'myId'>, Customer>(
    'clientes/celcash',
)
const modulos = new ApiService<Modulo>('clientes/modulos')
const limites = new ApiService<{
    ativos: number
    limite: number
}>('clientes/limites')

export const clientes = Object.assign(service, {
    celcash,
    modulos,
    limites,
})

const regexValidacaoTelefone = /^(\d{2})\s?(\d{4,5})\s?(\d{4})$/
const dddValidos = [
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '21',
    '22',
    '24',
    '27',
    '28',
    '31',
    '32',
    '33',
    '34',
    '35',
    '37',
    '38',
    '41',
    '42',
    '43',
    '44',
    '45',
    '46',
    '47',
    '48',
    '49',
    '51',
    '53',
    '54',
    '55',
    '61',
    '62',
    '63',
    '64',
    '65',
    '66',
    '67',
    '68',
    '69',
    '71',
    '73',
    '74',
    '75',
    '77',
    '79',
    '81',
    '82',
    '83',
    '84',
    '85',
    '86',
    '87',
    '88',
    '89',
    '91',
    '92',
    '93',
    '94',
    '95',
    '96',
    '97',
    '98',
    '99',
]

export const SwitchModuloSchema = zod.ModuloClienteSchema.pick({
    modulo: true,
    clienteId: true,
})

export const EditarClienteSchemaShape = AvaliacaoSchema.pick({
    nomeResponsavel: true,
    nomeFantasia: true,
    emailResponsavel: true,
    limiteFuncionarios: true,
}).extend({
    cnpj: zod.EmpresaSchema.shape.documento,
    razaoSocial: zod.EmpresaSchema.shape.razaoSocial,
    id: zod.ClienteSchema.shape.id,
    telefoneResponsavel: zod.ClienteSchema.shape.telefoneResponsavel,
    dtFimAtivacao: zod.FuncionarioSchema.shape.dtAdmissao,
    alterarPlano: z.boolean().optional(),
    nomeContato: zod.ClienteSchema.shape.nomeContato,
    suporteIntegrado: zod.ClienteSchema.shape.suporteIntegrado,
    emailContato: z
        .string()
        .trim()
        .optional()
        .nullish()
        .refine(
            value => !value || (value.length > 3 && value.includes('@')),
            'Email inválido',
        ),
    telefoneContato: z
        .string()
        .trim()
        .optional()
        .nullish()
        .refine(
            value => !value || value.match(regexValidacaoTelefone),
            'Telefone inválido',
        )
        .refine(value => {
            if (!value) return true
            const match = value.match(regexValidacaoTelefone)
            if (!match) return false
            const ddd = match[1]
            return dddValidos.includes(ddd)
        }, 'DDD inválido')
        .transform(value => (!value ? value : value.replace(/[^0-9]/g, ''))),
    PlanoDiferenciado: zod.PlanoDiferenciadoSchema.pick({
        limiteFuncionarios: true,
        periodicidade: true,
        valorCentavos: true,
    })
        .deepPartial()
        .extend({
            editar: z.boolean().optional(),
        })
        .optional(),
})

export const EditarClienteSchema = EditarClienteSchemaShape.transform(value => {
    if (
        !value.PlanoDiferenciado?.editar ||
        !value.PlanoDiferenciado.limiteFuncionarios
    )
        value.PlanoDiferenciado = undefined

    return value
})
